import type { MouseEventHandler, ReactNode } from 'react';
import React from 'react';
import { Link as RRDLink } from 'react-router-dom';
import { FormattedMessage } from 'react-intl';
import { css } from '@emotion/react';
import { legacyOnly, newDesignOnly } from 'constants/themes.constants';
import styled from 'types/theme.types';
import type { EmotionJSX } from '@emotion/react/types/jsx-namespace';

export interface LinkProps {
  messageId?: string | ReactNode;
  url?: string;
  plainText?: boolean;
  onClick?: MouseEventHandler<HTMLAnchorElement>;
  className?: string;
  download?: string;
  children?: ReactNode;
  openInNewWindow?: boolean;
}

const Link: React.FC<LinkProps> = ({
  messageId,
  url,
  plainText = false,
  onClick,
  className,
  children,
  openInNewWindow,
  ...props
}) => {
  const LinkText = children ? children : plainText ? messageId : <FormattedMessage id={messageId as string} />;

  if (url?.startsWith('/') && !openInNewWindow) {
    return (
      <StyledRRDLink to={url} className={className} {...props} onClick={onClick}>
        {LinkText}
      </StyledRRDLink>
    );
  }
  return (
    <StyledLink
      {...props}
      target="_blank"
      rel="noopener noreferrer"
      href={url}
      onClick={e => {
        e.stopPropagation();
        onClick?.(e);
      }}
      className={className}>
      {LinkText}
    </StyledLink>
  );
};

/* One treatment for external and internal alike. The legacy teal leaked in with its `!important`,
   which suppressed every hover (UI-polish review, links). The doubled class beats antd's global
   `a:hover`, whose cssinjs tag is injected after emotion's. */
const newDesignLink = newDesignOnly(css`
  color: var(--text-tint-lime);
  font-weight: 500;

  &&:hover {
    color: var(--text-tint-lime);
    text-decoration: underline;
  }
`);

const StyledLink = styled.a`
  ${legacyOnly(
    theme => css`
      color: ${theme.tealBlue} !important;
      font-weight: bold;
    `
  )}
  ${newDesignLink}
`;

const StyledRRDLink = styled(RRDLink)`
  ${legacyOnly(
    theme => css`
      color: ${theme.tealBlue} !important;
    `
  )}
  ${newDesignLink}
`;

export const setLink = (link: string, onClick?: () => void) => buildLink(link, { onClick });

export const buildLink = (link: string, props?: LinkProps) => ({
  a: (text: string | ReactNode) => <Link plainText messageId={text} url={link} {...props} />
});

export const setLinksByTag = (tagToLinkMap: Record<string, string>) => {
  const tagToLinkCallback: Record<string, (text: string | ReactNode) => EmotionJSX.Element> = {};

  Object.keys(tagToLinkMap).forEach(tag => {
    tagToLinkCallback[tag] = text => <Link messageId={text} url={tagToLinkMap[tag] ?? ''} plainText />;
  });

  return tagToLinkCallback;
};

export default Link;
